/**
 * DG CUSTOM (#15 + #45): shared "where is this shopper, and which store is nearest" resolver.
 *
 * Extracted so the PDP sold-out note and the store locator resolve position IDENTICALLY and share
 * one cache. They previously could not: the note had this logic baked into its custom element, and
 * a second copy in the locator would have drifted. The accept/ignore/dismiss/block distinction
 * below is subtle enough that having two versions of it is a real hazard.
 *
 * Position is resolved cheapest-and-most-accurate first:
 *   1. A store resolved earlier this browser, from localStorage — no request, no cost.
 *   2. Device geolocation. This is what shows the permission prompt. Free AND exact.
 *   3. Google's Geolocation API on IP — reached ONLY by shoppers who actively BLOCK.
 *
 * Rom's spec, 2026-08-10, re-affirmed 2026-08-18 after trialling the alternative:
 *   accept  -> free and exact
 *   ignore  -> nothing happens, costs nothing
 *   dismiss -> nothing happens, costs nothing
 *   block   -> billable IP lookup, so a store is still shown
 *
 * Paying out on a dismissal was built and reverted: dismissing is the commonest reaction to any
 * permission bubble, so it would have made the billable path the majority rather than the tail —
 * on a key that still has no quota cap.
 *
 * Step 3 is the only billable step and the ordering exists to keep it rare. The resolved store is
 * cached, so a browser can cost at most ONE lookup, ever.
 */
window.DGStoreLocate = (() => {
  /** Key under which the resolved store is cached (theme.storageUtil prefixes it "cc-"). */
  const STORE_KEY = 'dg-local-store';

  /**
   * Key under which the resolved POSITION is cached.
   *
   * Cached separately from the store because the two are not interchangeable: the note only ever
   * needs the single nearest store, but the locator needs the raw coordinates to rank all 52.
   * Without this the locator re-resolved from scratch on every visit, so a shopper who blocked
   * the prompt paid for one IP lookup on a product page and a SECOND one on the locator — for a
   * position we already knew. Rom spotted the redundancy, 2026-08-10.
   */
  const POSITION_KEY = 'dg-local-position';

  /**
   * How long a cached position stays trustworthy, in days, by how it was obtained.
   *
   * An IP guess expires far sooner than an answer the shopper gave us: it is only city-accurate
   * at best and is routinely wrong on VPNs, mobile carriers routing interstate, and corporate
   * proxies. Cached forever, a Melbourne shopper placed in Cairns would be told to ring Cairns on
   * every sold-out product, permanently, with no way back — worse than showing nothing.
   */
  const MAX_AGE_DAYS = { device: 90, address: 90, approx: 30, ip: 30 };

  /**
   * How coarse a device fix may be, in metres, before it counts as a guess rather than a fix.
   *
   * A machine with no Wi-Fi to scan makes the browser fall back to its own IP database and hand
   * that back as a successful fix, routinely hundreds of kilometres out. This radius is the only
   * thing in the reading that says so.
   */
  const MAX_ACCURACY_M = 25000;

  /** Session flag set after a failed lookup, so we attempt at most once per session. */
  const DECLINED_KEY = 'dg-local-store-declined';

  /** Google's IP-geolocation endpoint. The key is appended by locateByIp(). */
  const GEO_ENDPOINT = 'https://www.googleapis.com/geolocation/v1/geolocate?key=';

  /** Ceiling on a lookup, so a hung request never leaves a caller waiting forever. */
  const LOOKUP_TIMEOUT = 5000;

  /**
   * Ceiling on a device fix, applied only once permission is granted.
   *
   * Deliberately generous: a first fix with no cached one behind it can take several seconds, and
   * expiring early throws away a free exact answer in favour of a paid guess.
   */
  const DEVICE_TIMEOUT = 12000;

  /**
   * Great-circle distance in kilometres.
   * @param {number} lat1 - First latitude.
   * @param {number} lng1 - First longitude.
   * @param {number} lat2 - Second latitude.
   * @param {number} lng2 - Second longitude.
   * @returns {number}
   */
  function distanceKm(lat1, lng1, lat2, lng2) {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2
      + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;

    return 6371 * 2 * Math.asin(Math.sqrt(a));
  }

  /**
   * Splits a "lat,lng" metaobject string into numbers.
   * @param {string} value - The coordinates field.
   * @returns {{lat: number, lng: number}|null}
   */
  function parseCoordinates(value) {
    if (!value) return null;

    const parts = String(value).split(',');
    const lat = parseFloat(parts[0]);
    const lng = parseFloat(parts[1]);

    return Number.isNaN(lat) || Number.isNaN(lng) ? null : { lat, lng };
  }

  /**
   * Orders stores by distance from a position, nearest first.
   *
   * Takes a coordinate accessor rather than a fixed shape, because the note ships a compact
   * {t,p,c,u} record and the locator works with marker data — same maths, different objects.
   * @param {Array} stores - Anything with coordinates on it.
   * @param {{lat: number, lng: number}} position - The shopper's position.
   * @param {Function} getCoordinates - Returns the "lat,lng" string for one store.
   * @returns {Array<{store: object, km: number}>}
   */
  function byDistance(stores, position, getCoordinates) {
    return stores
      .map((store) => {
        const point = parseCoordinates(getCoordinates(store));
        if (!point) return null;

        return { store, km: distanceKm(position.lat, position.lng, point.lat, point.lng) };
      })
      .filter(Boolean)
      .sort((a, b) => a.km - b.km);
  }

  /**
   * Whether a cached entry is still within the lifetime for how it was obtained.
   *
   * Entries written before expiry existed carry no timestamp; those are kept rather than thrown
   * away, so shipping this does not blank every shopper's already-resolved store.
   * @param {object} entry - A cached record carrying `ts` and `src`.
   * @returns {boolean}
   */
  function isFresh(entry) {
    if (!entry || !entry.ts) return true;

    const days = MAX_AGE_DAYS[entry.src] || MAX_AGE_DAYS.ip;
    return (Date.now() - entry.ts) < days * 86400000;
  }

  /**
   * Reads the cached store, discarding anything stale or missing what callers need.
   * @returns {object|null}
   */
  function readCache() {
    try {
      const cached = theme.storageUtil.get(STORE_KEY, true);
      return cached && cached.t && cached.p && isFresh(cached) ? cached : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Caches a resolved store for every DG surface to reuse.
   * @param {object} store - The resolved store record.
   * @param {string} source - How the position behind it was obtained: device | address | ip.
   */
  function writeCache(store, source) {
    try {
      theme.storageUtil.set(STORE_KEY, { ...store, ts: Date.now(), src: source || 'ip' });
    } catch (e) {
      // Private mode or a full quota — not worth failing the feature over.
    }
  }

  /**
   * Reads the cached position, discarding anything stale.
   * @returns {{lat: number, lng: number, src: string}|null}
   */
  function readPosition() {
    try {
      const cached = theme.storageUtil.get(POSITION_KEY, true);

      return cached && Number.isFinite(cached.lat) && Number.isFinite(cached.lng) && isFresh(cached)
        ? cached
        : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Caches a resolved position so no DG surface ever pays to resolve it twice.
   * @param {{lat: number, lng: number}} position - The resolved position.
   * @param {string} source - How it was obtained: device | address | ip.
   */
  function writePosition(position, source) {
    try {
      theme.storageUtil.set(POSITION_KEY, {
        lat: position.lat,
        lng: position.lng,
        src: source,
        // Kept alongside the position so naming it is billed at most once per browser.
        city: position.city || null,
        ts: Date.now()
      });
    } catch (e) {
      // Private mode or a full quota — not worth failing the feature over.
    }
  }

  /**
   * Whether the shopper has given us an answer we can act on, and so whether the billable
   * fallback is warranted.
   *
   * The ONLY state that suppresses it is 'prompt' — a question still hanging in the air, either
   * because the bubble is open or because it was dismissed with the X without a decision.
   *
   * Both other states earn the fallback for different reasons: 'denied' is a deliberate refusal,
   * and 'granted' reaching this point means they consented but the device failed anyway (OS
   * location services off, or a stale permission, both of which happen). Treating a granted-but-
   * broken device as "no answer" would silently give the most co-operative shoppers nothing.
   *
   * Defaults to true when the Permissions API is unavailable (older Safari): there we cannot tell
   * the states apart, so we favour still showing a store over saving a call.
   * @returns {Promise<boolean>}
   */
  async function mayUseIpFallback() {
    return (await permissionState()) !== 'prompt';
  }

  /**
   * The browser's geolocation permission state, or 'unknown' where it cannot be read.
   *
   * 'unknown' covers browsers with no Permissions API and older Safari, which threw on the
   * geolocation name. Callers decide what to do with it rather than guessing here.
   * @returns {Promise<string>}
   */
  async function permissionState() {
    if (!navigator.permissions) return 'unknown';

    try {
      return (await navigator.permissions.query({ name: 'geolocation' })).state;
    } catch (e) {
      return 'unknown';
    }
  }

  /**
   * Device geolocation. This is the path that shows the permission prompt.
   *
   * Reports the fix's accuracy radius alongside it, because a "successful" fix says nothing about
   * whether it is worth trusting — see MAX_ACCURACY_M.
   * @returns {Promise<{lat: number, lng: number, accuracy: number}|null>}
   */
  async function locateByDevice() {
    if (!navigator.geolocation) return null;

    const state = await permissionState();
    if (state === 'denied') return null;

    const options = { maximumAge: 86400000, enableHighAccuracy: false };

    // A timeout is ONLY safe when permission is already granted, where a fix should come back
    // quickly and a hang means something is wrong.
    //
    // When the state is 'prompt' the shopper is sitting looking at a permission bubble, and
    // putting a clock on that is a bug we shipped once already: the timer expired while the
    // bubble was still open, the code read that as a refusal, and it called the BILLABLE lookup
    // for someone who was about to click Allow. Deciding takes as long as it takes.
    //
    // Trade-off, deliberate: a shopper who ignores the bubble entirely leaves this pending
    // forever and simply gets nothing. That costs nothing, which is the right way to be wrong.
    if (state === 'granted') options.timeout = DEVICE_TIMEOUT;

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        }),
        () => resolve(null),
        options
      );
    });
  }

  /**
   * IP-based position from Google's Geolocation API. Never prompts. BILLABLE.
   * @param {string} key - Google API key. Without one this step is skipped entirely.
   * @returns {Promise<{lat: number, lng: number}|null>}
   */
  async function locateByIp(key) {
    if (!key || !window.fetch) return null;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT);

    try {
      const response = await fetch(GEO_ENDPOINT + encodeURIComponent(key), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{"considerIp":true}',
        signal: controller.signal
      });

      if (!response.ok) return null;

      const location = (await response.json()).location;
      return location && Number.isFinite(location.lat) && Number.isFinite(location.lng)
        ? { lat: location.lat, lng: location.lng }
        : null;
    } catch (e) {
      // Aborted, offline, blocked by an ad blocker, or malformed JSON — all the same to us.
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Places API (New). The key is referrer-restricted, which Geocoding and the other older web
   * services refuse outright — this host is the only address lookup that answers the storefront.
   */
  const PLACES_HOST = 'https://places.googleapis.com/v1';

  /**
   * Ties a run of keystrokes and the Details call that ends them into one billable session.
   * Without it every keystroke is charged separately.
   */
  let placesSession = null;

  function placesToken() {
    if (!placesSession) {
      placesSession = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : String(Date.now()) + Math.random().toString(36).slice(2);
    }

    return placesSession;
  }

  /**
   * Suggests places for a partly-typed query.
   *
   * Australia-only, so "Melbourne" cannot offer Melbourne, Florida. Returns [] on every failure:
   * callers render a dropdown from it, and an empty list is the right answer for all of them.
   * @param {string} query - What the shopper has typed so far.
   * @param {string} key - Google API key.
   * @returns {Promise<Array<{id: string, main: string, secondary: string, text: string}>>}
   */
  async function suggestPlaces(query, key) {
    if (!query || !key || !window.fetch) return [];

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT);

    try {
      const response = await fetch(`${PLACES_HOST}/places:autocomplete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Goog-Api-Key': key },
        body: JSON.stringify({
          input: query,
          includedRegionCodes: ['au'],
          sessionToken: placesToken()
        }),
        signal: controller.signal
      });

      if (!response.ok) return [];

      const data = await response.json();

      return (data.suggestions || [])
        .map((item) => item.placePrediction)
        .filter((prediction) => prediction && prediction.placeId)
        .map((prediction) => ({
          id: prediction.placeId,
          main: (prediction.structuredFormat && prediction.structuredFormat.mainText.text) || '',
          secondary: (prediction.structuredFormat
            && prediction.structuredFormat.secondaryText
            && prediction.structuredFormat.secondaryText.text) || '',
          text: (prediction.text && prediction.text.text) || ''
        }));
    } catch (e) {
      return [];
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Turns a picked suggestion into coordinates.
   *
   * Field-masked to `location` alone — Place Details bills by field, and anything more would be
   * paid for and discarded. BILLABLE, and it closes the session token that the keystrokes opened.
   * @param {string} placeId - From suggestPlaces.
   * @param {string} key - Google API key.
   * @returns {Promise<{lat: number, lng: number}|null>}
   */
  async function placeLocation(placeId, key) {
    if (!placeId || !key || !window.fetch) return null;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT);
    const token = placesToken();

    placesSession = null;

    try {
      const response = await fetch(
        `${PLACES_HOST}/places/${encodeURIComponent(placeId)}?sessionToken=${encodeURIComponent(token)}`,
        {
          headers: { 'X-Goog-Api-Key': key, 'X-Goog-FieldMask': 'location' },
          signal: controller.signal
        }
      );

      if (!response.ok) return null;

      const data = await response.json();
      const location = data.location;

      return (location && Number.isFinite(location.latitude) && Number.isFinite(location.longitude))
        ? { lat: location.latitude, lng: location.longitude }
        : null;
    } catch (e) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Turns a typed suburb, postcode or address into coordinates.
   *
   * Resolves the FIRST suggestion, which is what someone who types a place and presses Enter
   * without opening the dropdown means. Gibberish suggests nothing and returns null.
   *
   * BILLABLE, one session per submitted search — fire it on submit or a debounce, never on raw
   * keystrokes.
   * @param {string} query - What the shopper typed.
   * @param {string} key - Google API key. Without one this returns null and callers fall back.
   * @returns {Promise<{lat: number, lng: number}|null>}
   */
  async function geocodeAddress(query, key) {
    const suggestions = await suggestPlaces(query, key);

    return suggestions.length ? placeLocation(suggestions[0].id, key) : null;
  }

  /**
   * Names the place a position falls in, for display only.
   *
   * Asks for locality-level results so the answer is "Brisbane", not a street address — this is
   * shown back to the shopper as "here is where we think you are", which a house number would
   * make alarming rather than helpful.
   *
   * BILLABLE, one call. Callers must cache the result on the position; nothing here is needed to
   * rank stores, which works from the coordinates alone.
   * @param {{lat: number, lng: number}} position - The position to name.
   * @param {string} key - Google API key. Without one this returns null and the box stays empty.
   * @returns {Promise<string|null>}
   */
  async function reverseGeocode(position, key) {
    if (!position || !key || !window.fetch) return null;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT);

    try {
      const url = 'https://maps.googleapis.com/maps/api/geocode/json'
        + `?latlng=${encodeURIComponent(`${position.lat},${position.lng}`)}`
        + '&result_type=locality|postal_town|administrative_area_level_2'
        + `&key=${encodeURIComponent(key)}`;

      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) return null;

      const data = await response.json();
      if (data.status !== 'OK' || !data.results || !data.results.length) return null;

      // Read the locality component rather than slicing formatted_address: the formatted string
      // varies by country and would put "Brisbane City QLD, Australia" in a suburb search box.
      const components = data.results[0].address_components || [];
      const locality = components.find((part) => part.types && part.types.indexOf('locality') !== -1);

      return (locality || components[0] || {}).long_name || null;
    } catch (e) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Resolves the shopper's position.
   *
   * Pass `approximate: false` to restrict the answer to a fix precise enough to name a nearest
   * store from — no IP lookup, and no coarse fix treated as exact. A surface that states a single
   * nearest store should use it: a confidently wrong store is worse than no store.
   * `skipDevice: true` is for a caller that has just made a strict attempt and is now asking what
   * else is available: the device has already had its go, and asking twice in one breath risks a
   * second permission prompt for nothing.
   *
   * `allowWhilePrompting: true` waives the rule that an unanswered bubble never earns the paid
   * lookup. Only for a caller that has already given the shopper a fair chance to answer and is
   * now filling a page that would otherwise sit empty — never on first ask.
   * @param {string} geoKey - Google API key for the IP fallback. Omit to disable that step.
   * @param {{approximate?: boolean, skipDevice?: boolean}} [options] - Defaults: guess allowed,
   *   device attempted.
   * @returns {Promise<{lat: number, lng: number, src: string}|null>}
   */
  async function resolvePosition(geoKey, options) {
    const allowApproximate = !options || options.approximate !== false;
    const skipDevice = Boolean(options && options.skipDevice);

    // Whatever any DG surface already worked out. The whole point of caching the position rather
    // than only the nearest store: the note and the locator need different things FROM the same
    // position, so resolving it twice was pure waste — and on the block path, paid waste.
    const cached = readPosition();
    if (cached && (allowApproximate || cached.src !== 'approx')) return cached;

    const fix = skipDevice ? null : await locateByDevice();

    if (fix) {
      // An accuracy radius wider than this is the browser's own IP guess wearing a device badge.
      // Cached either way so the shopper is not re-prompted, but never at the device lifetime.
      const exact = !Number.isFinite(fix.accuracy) || fix.accuracy <= MAX_ACCURACY_M;
      const src = exact ? 'device' : 'approx';

      writePosition(fix, src);
      return exact || allowApproximate ? { ...fix, src } : null;
    }

    if (!allowApproximate) return null;

    // An UNANSWERED prompt never earns the billable lookup.
    //
    // getCurrentPosition reports the same PERMISSION_DENIED whether the shopper clicked Block or
    // just dismissed the bubble with the X, but the two are not the same intent and Chrome does
    // distinguish them: Block persists as 'denied', a dismissal leaves the state at 'prompt'
    // because nothing was decided. Dismissing is the commonest reaction to any permission bubble,
    // so treating it as a refusal would send most shoppers down the paid path for a question they
    // never actually answered.
    const waived = Boolean(options && options.allowWhilePrompting);
    if (!waived && !(await mayUseIpFallback())) return null;

    const approximate = await locateByIp(geoKey);
    if (!approximate) return null;

    // Cached even though it is only a guess — a second billable call would be no more accurate
    // than the first. The shorter IP lifetime above is what stops a bad guess sticking forever.
    writePosition(approximate, 'approx');
    return { ...approximate, src: 'approx' };
  }

  return {
    STORE_KEY,
    POSITION_KEY,
    DECLINED_KEY,
    readPosition,
    writePosition,
    distanceKm,
    parseCoordinates,
    byDistance,
    permissionState,
    reverseGeocode,
    suggestPlaces,
    placeLocation,
    geocodeAddress,
    readCache,
    writeCache,
    resolvePosition
  };
})();
